import {
    Box,
    Button,
    Divider,
    ListItemText,
    Menu,
    MenuItem,
    SvgIcon,
    Typography
} from '@mui/material'
import { Fragment, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { SPACE_LABELS } from '@/features/docs/docsCategory'
import { categoriesInSpace, spacesWithContent } from '@/features/docs/docsNav'
import { useDocsNavContext } from '@/features/docs/docsNavContext'
import ChevronDownIcon from '@/icons/ChevronDown'

/**
 * The Categories control in the docs navbar.
 *
 * This is the piece that lets the sidebar stop carrying the whole site.
 * Because it lives in the navbar it is reachable from every docs page and
 * at every width, including below `lg` where the sidebar collapses into a
 * temporary drawer and the old nav was at its worst.
 *
 * Spaces are separated by a divider and a heading rather than merged:
 * fifty-nine operations pages interleaved with "Importing meter readings"
 * is a worse browse than either alone, and the readers do not overlap. A
 * reader with no operations access never sees that group at all, not even
 * its heading.
 */
const DocsCategoriesMenu = () => {
    const [anchorEl, setAnchorEl] = useState(null)
    const navigate = useNavigate()
    const { nav, loading } = useDocsNavContext()

    const spaces = spacesWithContent(nav)

    const go = (category) => {
        setAnchorEl(null)
        navigate(`/docs/category/${category.slug}`)
    }

    const renderCategory = (category) => (
        <MenuItem
            key={`${category.space}:${category.slug}`}
            onClick={() => go(category)}
            data-testid="docs-category-menu-item"
            data-category-slug={category.slug}
            data-space={category.space}
        >
            {/*
             * Decoration. `aria-hidden` keeps a screen reader from
             * announcing the glyph before every category name.
             */}
            <Box aria-hidden component="span" sx={{ mr: 1.5, fontSize: 18 }}>
                {category.emoji}
            </Box>
            <ListItemText
                primary={category.text}
                secondary={`${category.pages.length} ${
                    category.pages.length === 1 ? 'page' : 'pages'
                }`}
            />
        </MenuItem>
    )

    return (
        <>
            <Button
                color="inherit"
                disabled={loading}
                onClick={(event) => setAnchorEl(event.currentTarget)}
                data-testid="docs-categories-button"
                endIcon={
                    <SvgIcon fontSize="small">
                        <ChevronDownIcon />
                    </SvgIcon>
                }
                sx={{ textTransform: 'none' }}
            >
                Categories
            </Button>

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
                slotProps={{
                    paper: { sx: { minWidth: 300, maxHeight: '70vh' } }
                }}
            >
                {spaces.map((space, index) => (
                    <Fragment key={space}>
                        {index > 0 && <Divider sx={{ my: 1 }} />}
                        <Typography
                            color="text.secondary"
                            variant="caption"
                            sx={{ display: 'block', px: 2, py: 0.5 }}
                        >
                            {SPACE_LABELS[space]}
                        </Typography>
                        {categoriesInSpace(nav, space).map(renderCategory)}
                    </Fragment>
                ))}

                {!loading && spaces.length === 0 && (
                    <MenuItem disabled>No documentation available</MenuItem>
                )}
            </Menu>
        </>
    )
}

export default DocsCategoriesMenu
